import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Shield, X } from 'lucide-react';
import { isConsentPending, grantConsent, denyConsent, trackPageView } from '../../utils/analyticsTracker';

const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const hasToken = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
      if (isConsentPending() && !hasToken) {
        setVisible(true);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Toggle body class so other fixed elements (sticky CTA) can hide themselves
  useEffect(() => {
    if (visible) {
      document.body.classList.add('consent-banner-open');
    } else {
      document.body.classList.remove('consent-banner-open');
    }
    return () => document.body.classList.remove('consent-banner-open');
  }, [visible]);

  // Auto-hide when user logs in mid-session
  useEffect(() => {
    if (!visible) return;
    const check = () => {
      const hasToken = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
      if (hasToken) setVisible(false);
    };
    // Listen for storage changes + poll briefly after auth redirects
    window.addEventListener('storage', check);
    const poll = setInterval(check, 1000);
    return () => {
      window.removeEventListener('storage', check);
      clearInterval(poll);
    };
  }, [visible]);

  const handleAllow = () => {
    grantConsent();
    setVisible(false);
    trackPageView(window.location.pathname);
  };

  const handleDeny = () => {
    denyConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 z-[9999] animate-in slide-in-from-bottom duration-300"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Solid backdrop to prevent any bleed-through */}
      <div className="bg-white sm:bg-transparent">
        <div className="mx-auto max-w-3xl px-3 pb-3 pt-0 sm:px-6 sm:pb-4">
          <div className="relative bg-white border border-slate-200 rounded-2xl shadow-[0_-4px_24px_rgba(0,0,0,0.12)] p-4 sm:p-6">
            <button
              data-testid="consent-close-btn"
              onClick={handleDeny}
              className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-700 transition-colors rounded-full hover:bg-slate-100"
              aria-label="Sluiten"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3 sm:gap-4">
              <div className="shrink-0 mt-0.5 p-2 rounded-lg bg-blue-50">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>

              <div className="flex-1 min-w-0 pr-4">
                <p className="text-sm font-semibold text-slate-900 mb-1">
                  Cookies & Analytics
                </p>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mb-3 sm:mb-4">
                  Wij gebruiken analytics om onze website te verbeteren. Er worden geen persoonsgegevens gedeeld met derden.
                </p>

                <div className="flex items-center gap-2.5">
                  <Button
                    data-testid="consent-allow-btn"
                    onClick={handleAllow}
                    className="flex-1 sm:flex-none px-5 h-10 text-sm font-semibold bg-[#0B1F3B] text-white hover:bg-[#162d4f] rounded-lg transition-all"
                  >
                    Toestaan
                  </Button>
                  <Button
                    data-testid="consent-deny-btn"
                    onClick={handleDeny}
                    variant="outline"
                    className="flex-1 sm:flex-none px-5 h-10 text-sm font-semibold border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg transition-all"
                  >
                    Weigeren
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
